import { useCallback, useMemo, useState } from 'react';

export const useItemSelection = <T extends { id: string }>(items: T[], visibleItems: T[]) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(items.map(({ id }) => id)));

  const toggleItem = useCallback((id: string) => {
    setSelectedIds(previous => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);

      return next;
    });
  }, []);

  const selectVisible = useCallback(
    (isSelected: (item: T) => boolean) =>
      setSelectedIds(previous => {
        const next = new Set(previous);
        for (const item of visibleItems) {
          if (isSelected(item)) next.add(item.id);
          else next.delete(item.id);
        }

        return next;
      }),
    [visibleItems]
  );

  const selectAllVisible = useCallback(() => selectVisible(() => true), [selectVisible]);
  const deselectAllVisible = useCallback(() => selectVisible(() => false), [selectVisible]);

  const selectedItems = useMemo(() => items.filter(({ id }) => selectedIds.has(id)), [items, selectedIds]);

  return { selectedIds, selectedItems, toggleItem, selectVisible, selectAllVisible, deselectAllVisible };
};
